import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, Heart, PenSquare, Camera, Package, Info, History, LogOut, Shield, UserCog, Pencil, Link2, Copy, Bell, Mail, ThumbsDown, Users, Minus, Trash2, CopyCheck, Megaphone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DEFAULT_PANTRY } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import ProfileDialog from "@/components/ProfileDialog";
import { pushSupported, getPushSubscription, enablePush, disablePush } from "@/lib/push";

const UNIT_LABELS = { cs: "Cuillère à soupe (càs)", cc: "Cuillère à café (càc)", sachet: "Sachet", "pincée": "Pincée", filet: "Filet", verre: "Verre", paquet: "Paquet" };

function ConversionRow({ c, onSave, onReset }) {
  const [qty, setQty] = useState(c.to_qty);
  const [toUnit, setToUnit] = useState(c.to_unit);
  const dirty = String(qty) !== String(c.to_qty) || toUnit !== c.to_unit;
  return (
    <div className="flex items-center gap-2 rounded-2xl border border-black/5 bg-white p-2.5" data-testid={`conv-row-${c.unit}`}>
      <span className="min-w-0 flex-1 truncate text-sm font-medium">1 {UNIT_LABELS[c.unit] || c.unit}</span>
      <span className="text-sm" style={{ color: "var(--at-muted)" }}>≈</span>
      <Input type="number" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)}
        data-testid={`conv-qty-${c.unit}`} className="h-9 w-16 rounded-xl text-center" />
      <select value={toUnit} onChange={(e) => setToUnit(e.target.value)} data-testid={`conv-unit-${c.unit}`}
        className="h-9 rounded-xl border border-black/10 bg-white px-2 text-sm">
        {["g", "kg", "ml", "cl", "L"].map((u) => <option key={u} value={u}>{u}</option>)}
      </select>
      {dirty ? (
        <Button size="sm" data-testid={`conv-save-${c.unit}`} onClick={() => onSave(c, qty, toUnit)} className="h-9 rounded-xl px-3 text-white" style={{ background: "var(--at-sage)" }}>OK</Button>
      ) : c.is_custom ? (
        <button data-testid={`conv-reset-${c.unit}`} onClick={() => onReset(c)} className="text-xs" style={{ color: "var(--at-muted)" }}>défaut</button>
      ) : null}
    </div>
  );
}

export default function More() {
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuth();
  const [pantry, setPantry] = useState([]);
  const [dislikes, setDislikes] = useState([]);
  const [dislikeName, setDislikeName] = useState("");
  const [users, setUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [name, setName] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const [pushReady, setPushReady] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [reminderHour, setReminderHour] = useState(user?.reminder_hour ?? 18);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [conversions, setConversions] = useState([]);
  const [equivs, setEquivs] = useState([]);
  const [eqName, setEqName] = useState("");
  const [eqFrom, setEqFrom] = useState("sachet");
  const [eqQty, setEqQty] = useState("");
  const [eqTo, setEqTo] = useState("ml");
  const [annMessage, setAnnMessage] = useState("");
  const [annCurrent, setAnnCurrent] = useState(null);
  const [annBusy, setAnnBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported()) return;
    setPushReady(true);
    getPushSubscription().then((s) => setPushOn(!!s)).catch(() => {});
  }, []);

  const togglePush = async (val) => {
    setPushBusy(true);
    try {
      if (val) { await enablePush(); setPushOn(true); toast.success("Rappels du dîner activés"); }
      else { await disablePush(); setPushOn(false); toast.success("Rappels désactivés"); }
    } catch (e) {
      if (e.message === "denied") toast.error("Autorisez les notifications dans votre navigateur");
      else if (e.message === "unsupported") toast.error("Notifications non disponibles sur cet appareil");
      else toast.error("Impossible d'activer les rappels");
      setPushOn(false);
    } finally { setPushBusy(false); }
  };

  const changeReminderHour = async (h) => {
    const hour = parseInt(h, 10);
    setReminderHour(hour);
    try { await api.patch("/push/reminder-time", { hour }); toast.success(`Rappel réglé à ${hour}h`); }
    catch { toast.error("Impossible d'enregistrer l'heure"); }
  };

  const testPush = async () => {
    try {
      const { data } = await api.post("/push/test");
      if (data.sent > 0) toast.success("Notification de test envoyée 📲");
      else toast.error("Aucun appareil abonné. Activez d'abord les notifications.");
    } catch { toast.error("Impossible d'envoyer la notification de test"); }
  };

  const sendInvite = async () => {
    const em = inviteEmail.trim();
    if (!em.includes("@")) { toast.error("Entrez un email valide"); return; }
    setInviteBusy(true);
    try {
      const { data } = await api.post("/invite", { email: em });
      if (data.sent) toast.success(`Invitation envoyée à ${em}`);
      else { try { await navigator.clipboard.writeText(data.link); } catch (_) {} toast.success("Lien d'invitation copié"); }
      setInviteEmail("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally { setInviteBusy(false); }
  };

  const inviteLink = typeof window !== "undefined" ? window.location.origin + "/" : "";
  const shareInvite = async () => {
    try {
      if (navigator.share) await navigator.share({ title: "Rejoignez-moi sur À Table", text: "Crée ton compte sur À Table pour accéder à nos recettes de famille :", url: inviteLink });
      else { await navigator.clipboard.writeText(inviteLink); toast.success("Lien d'invitation copié"); }
    } catch (e) { /* annulé */ }
  };

  const load = () => {
    api.get("/pantry").then((r) => setPantry(r.data));
    api.get("/dislikes").then((r) => setDislikes(r.data));
    if (user?.is_super_admin) api.get("/users").then((r) => { setUsers(r.data); setUsersLoaded(true); }).catch(() => {});
    if (user?.is_super_admin) api.get("/unit-conversions").then((r) => setConversions(r.data)).catch(() => {});
    if (user?.is_super_admin) api.get("/equivalences").then((r) => setEquivs(r.data)).catch(() => {});
    if (user?.is_super_admin) api.get("/announcement").then((r) => { setAnnCurrent(r.data); setAnnMessage(r.data?.message || ""); }).catch(() => {});
  };
  useEffect(() => { load(); }, [user?.is_super_admin]); // eslint-disable-line

  const saveConversion = async (c, qty, toUnit) => {
    try {
      const { data } = await api.put("/unit-conversions", { unit: c.unit, to_qty: parseFloat(qty), to_unit: toUnit });
      setConversions((arr) => arr.map((x) => (x.unit === c.unit ? { ...x, to_qty: data.to_qty, to_unit: data.to_unit, is_custom: true } : x)));
      toast.success("Conversion enregistrée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const resetConversion = async (c) => {
    try {
      await api.delete(`/unit-conversions/${encodeURIComponent(c.unit)}`);
      const { data } = await api.get("/unit-conversions");
      setConversions(data);
      toast.success("Valeur par défaut rétablie");
    } catch { toast.error("Erreur"); }
  };

  const addEquiv = async () => {
    const nn = eqName.trim();
    const qty = parseFloat(eqQty);
    if (!nn) { toast.error("Nom de l'ingrédient requis"); return; }
    if (!qty || qty <= 0) { toast.error("Quantité invalide"); return; }
    try {
      await api.post("/equivalences", { normalized_name: nn, from_unit: eqFrom, to_qty: qty, to_unit: eqTo });
      const { data } = await api.get("/equivalences");
      setEquivs(data);
      setEqName(""); setEqQty("");
      toast.success("Correspondance enregistrée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const delEquiv = async (id) => {
    try {
      await api.delete(`/equivalences/${id}`);
      setEquivs((arr) => arr.filter((x) => x.id !== id));
    } catch { toast.error("Erreur"); }
  };

  const toggleShare = async (u, val) => {
    const res = await api.patch(`/users/${u.user_id}/permissions`, { can_share: val });
    setUsers((arr) => arr.map((x) => (x.user_id === u.user_id ? res.data : x)));
    toast.success(val ? `${u.name} peut partager des recettes` : `${u.name} ne partage plus`);
  };

  const saveAnnouncement = async () => {
    const msg = annMessage.trim();
    if (!msg) { toast.error("Écrivez un message"); return; }
    setAnnBusy(true);
    try {
      const { data } = await api.put("/announcement", { message: msg });
      setAnnCurrent(data);
      toast.success(data.notified > 0
        ? `Annonce publiée · notification envoyée à ${data.notified} abonné${data.notified > 1 ? "s" : ""}`
        : "Annonce publiée sur l'accueil");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setAnnBusy(false); }
  };
  const clearAnnouncement = async () => {
    setAnnBusy(true);
    try {
      await api.delete("/announcement");
      setAnnCurrent(null); setAnnMessage("");
      toast.success("Annonce retirée");
    } catch { toast.error("Erreur"); }
    finally { setAnnBusy(false); }
  };

  const add = async (n) => {
    const val = (n ?? name).trim();
    if (!val) return;
    if (pantry.some((p) => p.name.toLowerCase() === val.toLowerCase())) { setName(""); return; }
    await api.post("/pantry", { name: val });
    setName("");
    load();
  };

  const remove = async (id) => { await api.delete(`/pantry/${id}`); load(); };

  const changeHousehold = async (val) => {
    const n = Math.max(1, Math.min(20, val));
    setUser((u) => ({ ...u, household_size: n }));
    try { await api.patch("/auth/profile", { household_size: n }); }
    catch { toast.error("Impossible d'enregistrer"); }
  };

  const addDislike = async (n) => {
    const val = (n ?? dislikeName).trim();
    if (!val) return;
    if (dislikes.some((d) => d.name.toLowerCase() === val.toLowerCase())) { setDislikeName(""); return; }
    await api.post("/dislikes", { name: val });
    setDislikeName(""); load();
    toast.success(`« ${val} » ajouté à vos aversions`);
  };
  const removeDislike = async (id) => { await api.delete(`/dislikes/${id}`); load(); };

  const missingDefaults = DEFAULT_PANTRY.filter((d) => !pantry.some((p) => p.name.toLowerCase() === d.toLowerCase()));

  return (
    <div className="px-5 pt-8" data-testid="more-page">
      <header className="at-fade-up">
        <h1 className="font-display text-3xl font-bold">Plus</h1>
      </header>

      {/* Compte */}
      <section className="mt-5 flex items-center gap-3 rounded-2xl border border-black/5 bg-white p-4 at-shadow at-fade-up" data-testid="account-card">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full text-white" style={{ background: "var(--at-brand)" }}>
          {user?.picture && /^(https?:|\/)/.test(user.picture) ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0]?.toUpperCase() || "?")}
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 truncate font-semibold">
            {user?.name}
            {user?.is_admin && <span className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "var(--at-honey-light)", color: "#92400E" }}><Shield size={10} /> Admin</span>}
          </p>
          <p className="truncate text-xs" style={{ color: "var(--at-muted)" }}>{user?.email}</p>
        </div>
        <button data-testid="edit-profile-btn" onClick={() => setProfileOpen(true)} className="flex h-9 w-9 items-center justify-center rounded-xl at-press" style={{ color: "var(--at-text-2)" }}>
          <Pencil size={17} />
        </button>
        <Button data-testid="logout-btn" variant="outline" size="sm" onClick={logout} className="rounded-xl text-red-500 border-red-200">
          <LogOut size={16} className="mr-1" /> Sortir
        </Button>
      </section>

      {/* Nombre de personnes (réglage global des quantités) */}
      <section className="mt-4 rounded-2xl border border-black/5 bg-white p-4 at-shadow at-fade-up" data-testid="household-section">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: "var(--at-sage-light)", color: "var(--at-sage)" }}><Users size={20} /></span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold">Nombre de personnes</p>
            <p className="text-xs" style={{ color: "var(--at-muted)" }}>Adapte par défaut les quantités des recettes et des courses.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full" data-testid="household-minus" onClick={() => changeHousehold((user?.household_size || 2) - 1)}><Minus size={18} /></Button>
            <span className="w-8 text-center text-lg font-bold" data-testid="household-value">{user?.household_size || 2}</span>
            <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full" data-testid="household-plus" onClick={() => changeHousehold((user?.household_size || 2) + 1)}><Plus size={18} /></Button>
          </div>
        </div>
      </section>

      {/* Notifications */}
      {pushReady && (
        <section className="mt-4 rounded-2xl border border-black/5 bg-white p-4 at-shadow at-fade-up" data-testid="notif-section">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: "var(--at-honey-light)", color: "var(--at-honey)" }}><Bell size={20} /></span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold">Notifications</p>
              <p className="text-xs" style={{ color: "var(--at-muted)" }}>Recevez le rappel du dîner <b>et</b> les nouvelles annonces de la famille sur cet appareil.</p>
            </div>
            <Switch data-testid="notif-toggle" checked={pushOn} disabled={pushBusy} onCheckedChange={togglePush} />
          </div>
          {pushOn && (
            <>
              <div className="mt-3 flex items-center justify-between border-t border-black/5 pt-3">
                <span className="text-sm" style={{ color: "var(--at-text-2)" }}>Heure du rappel du dîner</span>
                <select data-testid="reminder-hour-select" value={reminderHour} onChange={(e) => changeReminderHour(e.target.value)} className="h-9 rounded-xl border border-black/10 bg-white px-2 text-sm font-medium">
                  {[16, 17, 18, 19, 20, 21].map((h) => <option key={h} value={h}>{h}h00</option>)}
                </select>
              </div>
              <button data-testid="notif-test-btn" onClick={testPush}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-semibold at-press"
                style={{ borderColor: "var(--at-honey)", color: "var(--at-honey)" }}>
                <Bell size={15} /> Envoyer une notification de test
              </button>
            </>
          )}
          {!pushOn && (
            <p className="mt-3 border-t border-black/5 pt-3 text-xs" style={{ color: "var(--at-muted)" }}>
              Activez pour être prévenu. Sur iPhone : l'app doit être installée sur l'écran d'accueil et les notifications autorisées.
            </p>
          )}
        </section>
      )}

      {/* Gestion des utilisateurs (super admin uniquement) */}
      {user?.is_super_admin && (
        <section className="mt-5 at-fade-up" data-testid="admin-users-section">
          <div className="mb-1 flex items-center gap-2">
            <UserCog size={20} style={{ color: "var(--at-brand)" }} />
            <h2 className="font-display text-xl font-semibold">Gestion des utilisateurs</h2>
          </div>
          <p className="mb-3 text-sm" style={{ color: "var(--at-text-2)" }}>
            Autorisez qui peut ajouter des recettes à la bibliothèque partagée. Les autres gardent leurs recettes privées.
          </p>

          {/* Lien d'invitation générique */}
          <div className="mb-4 rounded-2xl border border-dashed p-3" style={{ borderColor: "var(--at-sage)" }} data-testid="invite-card">
            <p className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--at-sage)" }}><Link2 size={16} /> Lien d'invitation</p>
            <p className="mb-2 mt-0.5 text-xs" style={{ color: "var(--at-text-2)" }}>Partagez ce lien : chacun crée son compte et accède aux recettes partagées.</p>
            <div className="flex gap-2">
              <Input readOnly value={inviteLink} data-testid="invite-link-input" className="h-10 rounded-xl border-black/10 bg-white text-xs" />
              <Button data-testid="copy-invite-btn" onClick={shareInvite} className="h-10 shrink-0 rounded-xl px-3 text-white at-press" style={{ background: "var(--at-sage)" }}>
                <Copy size={16} />
              </Button>
            </div>
            <div className="mt-3 border-t border-black/5 pt-3">
              <p className="mb-1.5 text-xs" style={{ color: "var(--at-text-2)" }}>Ou envoyez l'invitation par email (son inscription sera pré-remplie) :</p>
              <div className="flex gap-2">
                <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="email@exemple.fr" data-testid="invite-email-input" className="h-10 rounded-xl border-black/10 bg-white text-xs" />
                <Button data-testid="invite-send-btn" onClick={sendInvite} disabled={inviteBusy} className="h-10 shrink-0 rounded-xl px-3 text-white at-press" style={{ background: "var(--at-brand)" }}>
                  <Mail size={16} className="mr-1" /> Inviter
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white p-3 at-shadow">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: "var(--at-sage)" }}>
                  {u.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{u.name} {u.is_admin && <span className="text-[10px] font-bold" style={{ color: "var(--at-honey)" }}>(admin)</span>}</p>
                  <p className="truncate text-xs" style={{ color: "var(--at-muted)" }}>{u.email}</p>
                  <p className="text-[11px]" data-testid={`user-recipe-count-${u.user_id}`} style={{ color: "var(--at-muted)" }}>
                    {u.recipe_count || 0} recette{(u.recipe_count || 0) > 1 ? "s" : ""} ajoutée{(u.recipe_count || 0) > 1 ? "s" : ""}
                  </p>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-[10px] font-semibold uppercase" style={{ color: "var(--at-muted)" }}>Partage</span>
                  <Switch data-testid={`share-perm-${u.user_id}`} checked={u.can_share} disabled={u.is_admin}
                    onCheckedChange={(v) => toggleShare(u, v)} />
                </div>
              </div>
            ))}
            {users.length <= 1 && usersLoaded && <p className="text-sm" style={{ color: "var(--at-muted)" }}>Aucun autre utilisateur pour l'instant.</p>}
          </div>
        </section>
      )}

      {/* Annonce d'accueil (super admin uniquement) */}
      {user?.is_super_admin && (
        <section className="mt-5 at-fade-up" data-testid="announcement-section">
          <div className="mb-2 flex items-center gap-2">
            <Megaphone size={20} style={{ color: "var(--at-brand)" }} />
            <h2 className="font-display text-xl font-semibold">Annonce sur l'accueil</h2>
          </div>
          <p className="mb-3 text-sm" style={{ color: "var(--at-text-2)" }}>
            Un message visible par toute la famille en haut de l'accueil, avec une notification poussée aux membres abonnés aux rappels. {annCurrent ? "Annonce en cours de diffusion." : "Aucune annonce active."}
          </p>
          <textarea
            data-testid="announcement-input"
            value={annMessage}
            onChange={(e) => setAnnMessage(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Ex : De nouvelles recettes rapides et délicieuses vous attendent ! 🍽️"
            className="w-full rounded-2xl border border-black/10 bg-white p-3 text-sm"
          />
          <div className="mt-2 flex gap-2">
            <Button data-testid="announcement-save-btn" onClick={saveAnnouncement} disabled={annBusy}
              className="h-10 flex-1 rounded-xl text-white at-press" style={{ background: "var(--at-brand)" }}>
              {annCurrent ? "Mettre à jour" : "Publier"}
            </Button>
            {annCurrent && (
              <Button data-testid="announcement-clear-btn" onClick={clearAnnouncement} disabled={annBusy} variant="outline"
                className="h-10 rounded-xl px-4 at-press" style={{ borderColor: "var(--at-border)" }}>
                <Trash2 size={16} className="mr-1" /> Retirer
              </Button>
            )}
          </div>
        </section>
      )}

      {/* Conversions d'unités floues (super admin uniquement) */}
      {user?.is_super_admin && (
        <section className="mt-5 at-fade-up" data-testid="unit-conversions-section">
          <div className="mb-2">
            <h2 className="font-display text-xl font-semibold">Conversions d'unités</h2>
            <p className="text-sm" style={{ color: "var(--at-text-2)" }}>Poids/volume approximatif des unités floues (sachet, càs…) dans les courses.</p>
          </div>
          <div className="space-y-2">
            {conversions.map((c) => (
              <ConversionRow key={c.unit} c={c} onSave={saveConversion} onReset={resetConversion} />
            ))}
          </div>

          {/* Par ingrédient (prioritaire sur la table générale) */}
          <div className="mt-4">
            <p className="text-sm font-semibold">Par ingrédient</p>
            <p className="mb-2 text-xs" style={{ color: "var(--at-muted)" }}>Ex : 1 sachet de sauce soja = 20 ml, 1 sachet d'épices = 10 g. Prioritaire sur la table ci-dessus.</p>
            <div className="space-y-2">
              {equivs.map((e) => (
                <div key={e.id} data-testid={`equiv-row-${e.id}`} className="flex items-center gap-2 rounded-2xl border border-black/5 bg-white p-2.5">
                  <span className="min-w-0 flex-1 truncate text-sm">1 {e.from_unit} de <b>{e.normalized_name}</b> ≈ {e.to_qty} {e.to_unit}</span>
                  <button data-testid={`equiv-del-${e.id}`} onClick={() => delEquiv(e.id)} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full" style={{ background: "var(--at-elevated)", color: "#C94937" }}><X size={15} /></button>
                </div>
              ))}
            </div>
            <div className="mt-2 rounded-2xl border border-dashed p-2.5" style={{ borderColor: "var(--at-border)" }}>
              <Input data-testid="equiv-name" value={eqName} onChange={(e) => setEqName(e.target.value)} placeholder="Ingrédient (ex : sauce soja)" className="h-9 rounded-xl" />
              <div className="mt-2 flex items-center gap-2 text-sm">
                <span style={{ color: "var(--at-muted)" }}>1</span>
                <select data-testid="equiv-from" value={eqFrom} onChange={(e) => setEqFrom(e.target.value)} className="h-9 flex-1 rounded-xl border border-black/10 bg-white px-2">
                  {["sachet", "paquet", "cs", "cc", "pincée", "filet", "verre", "pot", "gousse"].map((u) => <option key={u} value={u}>{UNIT_LABELS[u] || u}</option>)}
                </select>
                <span style={{ color: "var(--at-muted)" }}>≈</span>
                <Input data-testid="equiv-qty" type="number" inputMode="decimal" value={eqQty} onChange={(e) => setEqQty(e.target.value)} placeholder="20" className="h-9 w-16 rounded-xl text-center" />
                <select data-testid="equiv-to" value={eqTo} onChange={(e) => setEqTo(e.target.value)} className="h-9 rounded-xl border border-black/10 bg-white px-2">
                  {["g", "kg", "ml", "cl", "L"].map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <Button data-testid="equiv-add" onClick={addEquiv} className="mt-2 h-9 w-full rounded-xl text-white" style={{ background: "var(--at-brand)" }}><Plus size={16} className="mr-1" /> Ajouter</Button>
            </div>
          </div>
        </section>
      )}

      {/* Raccourcis */}
      <div className="mt-5 grid grid-cols-3 gap-3 at-fade-up" style={{ animationDelay: "40ms" }}>
        {[
          { label: "Scanner", icon: Camera, to: "/scan", fg: "var(--at-brand)", bg: "var(--at-brand-light)" },
          { label: "Ajout manuel", icon: PenSquare, to: "/add", fg: "var(--at-sage)", bg: "var(--at-sage-light)" },
          { label: "Favoris", icon: Heart, to: "/recipes", fg: "#C94937", bg: "var(--at-brand-light)" },
          { label: "Historique", icon: History, to: "/history", fg: "var(--at-honey)", bg: "var(--at-honey-light)" },
          { label: "Corbeille", icon: Trash2, to: "/trash", fg: "#8C7B75", bg: "var(--at-elevated)" },
          { label: "Doublons", icon: CopyCheck, to: "/duplicates", fg: "var(--at-honey)", bg: "var(--at-honey-light)" },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <button key={s.label} data-testid={`more-${s.label}`} onClick={() => navigate(s.to)}
              className="at-press flex flex-col items-center gap-2 rounded-2xl border border-black/5 bg-white p-4 at-shadow">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: s.bg, color: s.fg }}><Icon size={22} /></span>
              <span className="text-xs font-semibold">{s.label}</span>
            </button>
          );
        })}
      </div>

      {/* Mes basiques */}
      <section className="mt-7 at-fade-up" style={{ animationDelay: "90ms" }}>
        <div className="mb-1 flex items-center gap-2">
          <Package size={20} style={{ color: "var(--at-sage)" }} />
          <h2 className="font-display text-xl font-semibold">Mes basiques</h2>
        </div>
        <p className="mb-3 text-sm" style={{ color: "var(--at-text-2)" }}>
          Les produits que vous avez déjà chez vous. Ils seront masqués de la liste de courses.
        </p>

        <div className="flex gap-2">
          <Input data-testid="pantry-input" value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Ex : Huile d'olive"
            className="h-12 rounded-2xl border-black/10 bg-white" />
          <Button data-testid="add-pantry-btn" onClick={() => add()} className="h-12 shrink-0 rounded-2xl px-4 text-white at-press" style={{ background: "var(--at-sage)" }}>
            <Plus size={20} />
          </Button>
        </div>

        {missingDefaults.length > 0 && (
          <div className="mt-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Suggestions</p>
            <div className="flex flex-wrap gap-2">
              {missingDefaults.map((d) => (
                <button key={d} data-testid={`suggest-${d}`} onClick={() => add(d)}
                  className="rounded-full border border-dashed px-3 py-1.5 text-sm at-press" style={{ borderColor: "var(--at-border)", color: "var(--at-text-2)" }}>
                  + {d}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {pantry.map((p) => (
            <span key={p.id} data-testid={`pantry-item-${p.id}`} className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium" style={{ background: "var(--at-sage-light)", color: "var(--at-sage)" }}>
              {p.name}
              <button onClick={() => remove(p.id)} data-testid={`remove-pantry-${p.id}`}><X size={14} /></button>
            </span>
          ))}
          {pantry.length === 0 && <p className="text-sm" style={{ color: "var(--at-muted)" }}>Aucun basique pour l'instant.</p>}
        </div>
      </section>

      {/* Je n'aime pas */}
      <section className="mt-7 at-fade-up" style={{ animationDelay: "110ms" }} data-testid="dislikes-section">
        <div className="mb-1 flex items-center gap-2">
          <ThumbsDown size={20} style={{ color: "var(--at-brand)" }} />
          <h2 className="font-display text-xl font-semibold">Je n'aime pas</h2>
        </div>
        <p className="mb-3 text-sm" style={{ color: "var(--at-text-2)" }}>
          Vos aversions personnelles. Une épice détestée est retirée de vos courses ; un ingrédient principal détesté grise la recette et l'exclut de l'auto-remplissage.
        </p>
        <div className="flex gap-2">
          <Input data-testid="dislike-input" value={dislikeName} onChange={(e) => setDislikeName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addDislike()} placeholder="Ex : Coriandre, champignons…"
            className="h-12 rounded-2xl border-black/10 bg-white" />
          <Button data-testid="add-dislike-btn" onClick={() => addDislike()} className="h-12 shrink-0 rounded-2xl px-4 text-white at-press" style={{ background: "var(--at-brand)" }}>
            <Plus size={20} />
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {dislikes.map((d) => (
            <span key={d.id} data-testid={`dislike-item-${d.id}`} className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium" style={{ background: "var(--at-blush, #FBE7E3)", color: "var(--at-brand)" }}>
              {d.name}
              <button onClick={() => removeDislike(d.id)} data-testid={`remove-dislike-${d.id}`}><X size={14} /></button>
            </span>
          ))}
          {dislikes.length === 0 && <p className="text-sm" style={{ color: "var(--at-muted)" }}>Aucune aversion pour l'instant.</p>}
        </div>
      </section>

      {/* À propos */}
      <section className="mt-8 rounded-2xl border border-black/5 bg-white p-4 at-shadow at-fade-up">
        <div className="flex items-start gap-2">
          <Info size={18} style={{ color: "var(--at-muted)" }} className="mt-0.5" />
          <div>
            <p className="font-display text-lg font-semibold">À Table</p>
            <p className="text-sm" style={{ color: "var(--at-text-2)" }}>
              Numérisez vos fiches recettes, planifiez vos repas et obtenez une liste de courses intelligente. Fait avec soin pour toute la famille.
            </p>
          </div>
        </div>
      </section>

      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </div>
  );
}
